import { useState } from 'react';
import { Icon, Stack, Text, tokens } from '@/design';
import {
  applyPolygonSymbolPreset,
  polygonPatternSvgDataUrl,
  polygonSymbolPresets,
  type PolygonSymbolFamily,
  type PolygonSymbolState,
} from './polygonSymbolRegistry';

export interface PolygonSymbolPickerProps {
  readonly value: PolygonSymbolState;
  readonly fillOpacity?: number;
  readonly strokeColor?: string;
  readonly strokeWidth?: number;
  readonly readOnly: boolean;
  readonly compact?: boolean;
  readonly onChange: (next: PolygonSymbolState) => void;
  readonly 'data-testid'?: string;
}

/** 32px spacing token minus the two one-pixel edges requested by the design. */
export const POLYGON_SYMBOL_PREVIEW_SIZE =
  tokens.space[6] - tokens.border.width.thin * 2;

export function PolygonSymbolPicker({
  value,
  fillOpacity = 1,
  strokeColor = tokens.color.ink.secondary,
  strokeWidth = tokens.border.width.thin,
  readOnly,
  compact = false,
  onChange,
  'data-testid': testId = 'polygon-symbol-picker',
}: PolygonSymbolPickerProps) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<Record<PolygonSymbolFamily, boolean>>({
    '2d': true,
    '3d-material': true,
  });

  const toggle = (
        <button
          type="button"
          aria-label="Polygon symbol"
          aria-expanded={open}
          aria-haspopup="dialog"
          disabled={readOnly}
          onClick={() => setOpen((current) => !current)}
          data-testid={`${testId}-toggle`}
          style={toggleStyle(readOnly)}
        >
          <span
            style={{
              display: 'inline-flex',
              width: POLYGON_SYMBOL_PREVIEW_SIZE,
              height: POLYGON_SYMBOL_PREVIEW_SIZE,
              boxSizing: 'border-box',
              overflow: 'hidden',
              border: `${Math.max(0, strokeWidth)}px solid ${strokeColor}`,
            }}
          >
            <img
              src={polygonPatternSvgDataUrl(value, POLYGON_SYMBOL_PREVIEW_SIZE)}
              width={POLYGON_SYMBOL_PREVIEW_SIZE}
              height={POLYGON_SYMBOL_PREVIEW_SIZE}
              alt=""
              aria-hidden="true"
              style={{
                display: 'block',
                width: '100%',
                height: '100%',
                opacity: Math.max(0, Math.min(1, fillOpacity)),
              }}
            />
          </span>
          <Icon name={open ? 'expand_less' : 'expand_more'} size={20} decorative />
        </button>
  );

  const gallery = open ? (
        <div
          role="dialog"
          aria-label="Polygon symbol gallery"
          data-testid={`${testId}-gallery`}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: tokens.space[2],
            padding: tokens.space[2],
            border: tokens.border.thin,
            borderRadius: tokens.radius.md,
            background: tokens.color.surface[1],
            ...(compact
              ? {
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  zIndex: tokens.zIndex.tooltip,
                  minWidth: tokens.layout.popoverMd,
                  boxShadow: tokens.elevation[2],
                }
              : {}),
          }}
        >
          <SymbolSection
            title="2D symbols"
            family="2d"
            expanded={expanded['2d']}
            value={value}
            readOnly={readOnly}
            onToggle={() =>
              setExpanded((current) => ({ ...current, '2d': !current['2d'] }))
            }
            onChange={onChange}
          />
          <SymbolSection
            title="3D materials"
            family="3d-material"
            expanded={expanded['3d-material']}
            value={value}
            readOnly={readOnly}
            onToggle={() =>
              setExpanded((current) => ({
                ...current,
                '3d-material': !current['3d-material'],
              }))
            }
            onChange={onChange}
          />
        </div>
      ) : null;

  if (compact) {
    return (
      <div
        data-testid={testId}
        style={{ position: 'relative', display: 'inline-flex' }}
      >
        {toggle}
        {gallery}
      </div>
    );
  }

  return (
    <Stack direction="column" gap={2} data-testid={testId}>
      <Stack direction="row" align="center" gap={2}>
        <Text variant="caption" as="span">
          Symbol
        </Text>
        {toggle}
      </Stack>
      {gallery}
    </Stack>
  );
}

function SymbolSection({
  title,
  family,
  expanded,
  value,
  readOnly,
  onToggle,
  onChange,
}: {
  readonly title: string;
  readonly family: PolygonSymbolFamily;
  readonly expanded: boolean;
  readonly value: PolygonSymbolState;
  readonly readOnly: boolean;
  readonly onToggle: () => void;
  readonly onChange: (next: PolygonSymbolState) => void;
}) {
  return (
    <section>
      <button
        type="button"
        aria-expanded={expanded}
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[1],
          width: '100%',
          minHeight: tokens.density.touchMin,
          padding: 0,
          border: 0,
          background: 'transparent',
          color: tokens.color.ink.primary,
          cursor: 'pointer',
          fontFamily: tokens.font.family.ui,
          fontSize: tokens.font.size.sm,
          fontWeight: tokens.font.weight.semibold,
          textAlign: 'left',
        }}
      >
        <Icon name={expanded ? 'expand_more' : 'chevron_right'} size={20} decorative />
        {title}
      </button>
      {expanded ? (
        <div
          role="radiogroup"
          aria-label={title}
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${tokens.space[8]}px, 1fr))`,
            gap: tokens.space[2],
          }}
        >
          {polygonSymbolPresets(family).map((preset) => {
            const selected = preset.id === value.presetId;
            return (
              <button
                key={preset.id}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={preset.label}
                title={preset.description}
                disabled={readOnly}
                onClick={() => onChange(applyPolygonSymbolPreset(value, preset.id))}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: tokens.space[1],
                  minWidth: 0,
                  padding: tokens.space[1],
                  border: selected
                    ? `${tokens.border.width.thin}px solid ${tokens.color.accent.primary}`
                    : `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
                  borderRadius: tokens.radius.sm,
                  background: selected ? tokens.color.surface[3] : tokens.color.surface[2],
                  color: tokens.color.ink.primary,
                  cursor: readOnly ? 'not-allowed' : 'pointer',
                }}
              >
                <img
                  src={polygonPatternSvgDataUrl(preset, tokens.space[7])}
                  width={tokens.space[7]}
                  height={tokens.space[7]}
                  alt=""
                  aria-hidden="true"
                  style={{
                    display: 'block',
                    border: `${tokens.border.width.thin}px solid ${tokens.color.ink.muted}`,
                  }}
                />
                <span
                  style={{
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontFamily: tokens.font.family.ui,
                    fontSize: tokens.font.size.xs,
                  }}
                >
                  {preset.label}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

function toggleStyle(disabled: boolean) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.space[1],
    minWidth: tokens.density.touchMin,
    minHeight: tokens.density.touchMin,
    padding: tokens.space[1],
    border: tokens.border.thin,
    borderRadius: tokens.radius.sm,
    background: tokens.color.surface[2],
    color: tokens.color.ink.primary,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
  } as const;
}
